/**
 * Bakoverkompatibilitet mellom Compass-visninger og eldre formater.
 * Speiler backend ViewSpecProjection.cs: en «enkel» graf (native chart uten transform, eller
 * Vega-Lite med én bar/line/area/arc og direkte x/y) projiseres til de gamle ChartConfig-feltene
 * som e-post, digest og eldre widget-moduser leser.
 */
import type { ColumnDefinition } from '../../../../../services/claudeQueryService';
import type { DatasetMap, ViewSpec } from './types';

const WIDGET_CHART_TYPES: Record<string, string> = {
    bar: 'bar', line: 'line', pie: 'pie', area: 'area', 'horizontal-bar': 'bar', 'stacked-bar': 'bar'
};

interface SimpleChart { chartType: string; title: string; xKey: string; series: { name: string; dataKey: string }[] }

export function firstSimpleChart(view: ViewSpec): SimpleChart | null {
    for (const panel of view.panels) {
        const title = panel.title ?? view.title ?? '';
        if (panel.kind === 'chart' && !panel.transform && panel.config) {
            const x = panel.config.xAxis?.dataKey;
            const series = (panel.config.series ?? []).filter((s: any) => s?.dataKey).map((s: any) => ({ name: s.name ?? s.dataKey, dataKey: s.dataKey }));
            if (panel.config.chartType && panel.config.chartType !== 'gauge' && x && series.length)
                return { chartType: panel.config.chartType, title, xKey: x, series };
        }
        if (panel.kind === 'vega-lite' && panel.spec && !panel.spec.layer && !panel.spec.transform) {
            const mark = typeof panel.spec.mark === 'string' ? panel.spec.mark : panel.spec.mark?.type;
            const enc = panel.spec.encoding ?? {};
            if (enc.x?.aggregate || enc.y?.aggregate || enc.theta?.aggregate) continue;
            // timeUnit/bin grupperer og color deler i serier i Vega — den enkle grafen ville vist rå rader
            if (enc.x?.timeUnit || enc.y?.timeUnit || enc.x?.bin || enc.y?.bin || (mark !== 'arc' && enc.color?.field)) continue;
            if (mark === 'arc' && enc.theta?.field && enc.color?.field)
                return { chartType: 'pie', title, xKey: enc.color.field, series: [{ name: enc.theta.field, dataKey: enc.theta.field }] };
            const type = ({ bar: 'bar', line: 'line', area: 'area' } as Record<string, string>)[mark];
            if (type && enc.x?.field && enc.y?.field) {
                return enc.x.type === 'quantitative'
                    ? { chartType: 'horizontal-bar', title, xKey: enc.y.field, series: [{ name: enc.x.field, dataKey: enc.x.field }] }
                    : { chartType: type, title, xKey: enc.x.field, series: [{ name: enc.y.field, dataKey: enc.y.field }] };
            }
        }
    }
    return null;
}

/**
 * ChartConfig-innhold for en lagret rapport: legacy-felter (hvis en enkel graf finnes) + view.
 * Det primære datasettet (indeks 0) bruker rapportens GeneratedSQL, så sql nulles der.
 */
export function chartConfigForSave(view: ViewSpec): { chartConfig: string; exportFormat: 'chart' | 'table' } {
    const chart = firstSimpleChart(view);
    const legacy = chart && WIDGET_CHART_TYPES[chart.chartType]
        ? {
            chartType: WIDGET_CHART_TYPES[chart.chartType],
            title: chart.title,
            xAxis: { dataKey: chart.xKey },
            yAxis: { dataKey: chart.series[0].dataKey },
            series: chart.series
        }
        : {};
    const stored: ViewSpec = {
        ...view,
        datasets: view.datasets.map((d, i) => (i === 0 ? { ...d, sql: null } : d))
    };
    return { chartConfig: JSON.stringify({ ...legacy, view: stored }), exportFormat: 'chartType' in legacy ? 'chart' : 'table' };
}

/**
 * Lager en visning av en eldre rapport (ChartConfig uten view) for den nye rendreren — bare for visning,
 * ingenting lagres før brukeren lagrer.
 */
export function viewFromLegacyReport(name: string, chartConfig: string | null | undefined, exportFormat: string | undefined, columns: ColumnDefinition[]): ViewSpec {
    let legacy: any = null;
    try { legacy = chartConfig ? JSON.parse(chartConfig) : null; } catch { legacy = null; }
    const panels: ViewSpec['panels'] = [];
    const items: ViewSpec['layout']['items'] = [];
    if (exportFormat === 'chart' && legacy?.chartType) {
        const xKey = legacy.xAxis?.dataKey ?? columns.find(c => c.dataType !== 'number')?.name;
        const series = (legacy.series?.length ? legacy.series : [{ name: legacy.yAxis?.dataKey, dataKey: legacy.yAxis?.dataKey }])
            .filter((s: any) => s?.dataKey)
            .map((s: any) => ({ name: s.name ?? s.dataKey, dataKey: s.dataKey }));
        if (xKey && series.length) {
            panels.push({ id: 'graf', kind: 'chart', dataset: 'main', title: legacy.title, config: { chartType: legacy.chartType, xAxis: { dataKey: xKey }, series } });
            items.push({ panel: 'graf', x: 0, y: 0, w: 12, h: 4 });
        }
    }
    panels.push({ id: 'tabell', kind: 'table', dataset: 'main' });
    items.push({ panel: 'tabell', x: 0, y: items.length ? 4 : 0, w: 12, h: 5 });
    return {
        specVersion: 1,
        title: name,
        createdBy: 'user',
        datasets: [{ id: 'main', sql: null }],
        layout: { columns: 12, items },
        panels
    };
}

/** Datasett-kart med bare det primære datasettet (eldre rapporter). */
export function primaryDataset(columns: ColumnDefinition[], rows: Record<string, any>[]): DatasetMap {
    return { main: { id: 'main', columns, rows, rowCount: rows.length } };
}
